import React, { useMemo } from 'react';
import './QualityCheckResultModal.css';

interface QualityCheckResultModalProps {
  isOpen: boolean;
  onClose: () => void;
  qualityScore: number;
  qualityReport: string;
  submissionId: number;
}

const QualityCheckResultModal: React.FC<QualityCheckResultModalProps> = React.memo(({
  isOpen,
  onClose,
  qualityScore,
  qualityReport,
  submissionId
}) => {
  console.log('🔍 DEBUG: Modal render - qualityReport length:', qualityReport?.length, 'qualityScore:', qualityScore, 'isOpen:', isOpen, 'submissionId:', submissionId);

  // Filter out visualization blocks from the report
  const filterVisualizationBlocks = (text: string): string => {
    const lines = text.split('\n');
    const filteredLines: string[] = [];
    let skipSection = false;
    let inCodeBlock = false;
    let inVisualizationJSON = false;
    
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      const trimmedLine = line.trim();
      
      // Check if this is a visualization header
      if (trimmedLine.startsWith('### Visualization') || trimmedLine.startsWith('### Chart')) {
        skipSection = true;
        continue;
      }
      
      // Check if we're entering/exiting a code block
      if (trimmedLine.startsWith('```')) {
        inCodeBlock = !inCodeBlock;
        if (skipSection) {
          continue; // Skip code blocks in visualization sections
        }
      }
      
      // Check for standalone JSON visualization blocks
      if (trimmedLine === '{' && !inCodeBlock) {
        // Look ahead to see if this looks like a visualization JSON
        let jsonContent = '';
        let braceCount = 0;
        let isVisualization = false;
        
        for (let j = i; j < Math.min(i + 20, lines.length); j++) {
          const lookAheadLine = lines[j].trim();
          jsonContent += lookAheadLine + ' ';
          
          // Count braces to find the end of JSON
          braceCount += (lookAheadLine.match(/\{/g) || []).length;
          braceCount -= (lookAheadLine.match(/\}/g) || []).length;
          
          // Check if this JSON contains visualization keywords
          if (lookAheadLine.includes('"type":') && 
              (lookAheadLine.includes('"bar"') || lookAheadLine.includes('"line"') || 
               lookAheadLine.includes('"pie"') || lookAheadLine.includes('"chart"'))) {
            isVisualization = true;
          }
          
          if (braceCount === 0 && j > i) {
            break; // Found the end of JSON
          }
        }
        
        if (isVisualization) {
          inVisualizationJSON = true;
          continue; // Skip this line
        }
      }
      
      // If we're in a visualization JSON block, skip until we find the closing brace
      if (inVisualizationJSON) {
        if (trimmedLine === '}') {
          inVisualizationJSON = false;
        }
        continue;
      }
      
      // If we're in a visualization section, skip until we hit another section or end
      if (skipSection) {
        // Check if we've reached a new section (starts with ## or ###)
        if (trimmedLine.startsWith('##') && !trimmedLine.startsWith('### Visualization') && !trimmedLine.startsWith('### Chart')) {
          skipSection = false;
          filteredLines.push(line);
        }
        continue;
      }
      
      // Add the line if we're not skipping
      filteredLines.push(line);
    }
    
    return filteredLines.join('\n');
  };

  // Parse the quality report to extract structured information
  const parseQualityReport = (report: string) => {
    // First filter out visualization blocks
    const filteredReport = filterVisualizationBlocks(report);
    
    try {
      // Try to parse as JSON first
      const parsed = JSON.parse(filteredReport);
      return parsed;
    } catch {
      // If not JSON, treat as plain text and try to extract sections
      const sections: { [key: string]: string } = {};
      
      // Common section patterns in AI reports
      const sectionPatterns = [
        'Summary:',
        'Key Issues:',
        'Recommendations:',
        'Data Quality Issues:',
        'Missing Data:',
        'Validation Results:',
        'Analysis:',
        'Findings:',
        'Issues Found:',
        'Suggestions:',
        'Key Metrics Overview:',
        'Data Integrity Observations:',
        'Conclusion:',
        // New patterns for enhanced quality reports
        'Overall Assessment:',
        'Rules Violated:',
        'Additional Quality Issues:',
        'Total Points Deducted:',
        'Final Quality Score:'
      ];

      let currentSection = 'Analysis';
      let currentContent = '';
      
      const lines = filteredReport.split('\n');
      

      
      for (const line of lines) {
        const trimmedLine = line.trim();
        
        // Check if this line is a section header (handle markdown formatting)
        const matchedPattern = sectionPatterns.find(pattern => {
          const cleanPattern = pattern.toLowerCase().replace(':', '');
          const cleanLine = trimmedLine.toLowerCase()
            .replace(/\*\*/g, '') // Remove markdown bold
            .replace(/:/g, ''); // Remove colons
          return cleanLine.includes(cleanPattern);
        });
        
        if (matchedPattern) {
          // Save previous section
          if (currentContent.trim()) {
            sections[currentSection] = currentContent.trim();
          }
          
          // Start new section
          currentSection = matchedPattern.replace(':', '');
          currentContent = '';
        } else if (trimmedLine) {
          currentContent += (currentContent ? '\n' : '') + trimmedLine;
        }
      }
      
      // Save the last section
      if (currentContent.trim()) {
        sections[currentSection] = currentContent.trim();
      }
      
      // If no sections were found, put everything in Analysis
      if (Object.keys(sections).length === 0) {
        sections['Analysis'] = report;
      }
      
      // Remove Overview section if it exists
      if (sections['Overview']) {
        delete sections['Overview'];
      }
      

      
      return sections;
    }
  };

  const getScoreColor = (score: number) => {
    // Neon palette to match AR style
    if (score >= 85) return '#00e5ff'; // Cyan - Excellent
    if (score >= 70) return '#00b4ff'; // Blue - Good
    if (score >= 55) return '#ffc300'; // Amber - Needs attention
    return '#ff2d95'; // Magenta - Critical
  };

  const getScoreLabel = (score: number) => {
    if (score >= 90) return 'Excellent';
    if (score >= 80) return 'Good';
    if (score >= 70) return 'Fair';
    if (score >= 60) return 'Below Average';
    if (score >= 40) return 'Poor';
    return 'Critical Issues';
  };

  const reportSections = useMemo(() => {
    const sections = parseQualityReport(qualityReport);
    

    
    return sections;
  }, [qualityReport]);

  // Extract quick stats for the header chips (violations count and total deductions)
  const { violationsCount, totalPointsDeducted } = useMemo(() => {
    const rulesText = `${String(reportSections['Rules Violated'] || '')}\n${String(
      reportSections['Analysis'] || ''
    )}`;

    // Count rules
    const ruleMatches = rulesText.match(/\*\*Rule\*\*:/g) || [];
    const count = ruleMatches.length;

    // Sum points deducted across all "Points Lost" lines
    const pointsRegex = /\*\*Points Lost\*\*:\s*([^\n]+)/g;
    let total = 0;
    let match: RegExpExecArray | null;
    // eslint-disable-next-line no-cond-assign
    while ((match = pointsRegex.exec(rulesText)) !== null) {
      const text = match[1] || '';
      // Normalize unicode minus and extract the first integer found
      const normalized = text.replace(/[–—−]/g, '-');
      const numMatch = normalized.match(/-?\s*(\d+)/);
      if (numMatch && numMatch[1]) {
        const value = parseInt(numMatch[1], 10);
        // Points Lost are presented as negatives; ensure we subtract positively
        total += value;
      }
    }

    // If totals were listed separately in the report, prefer that
    const totalSection = String(reportSections['Total Points Deducted'] || '');
    const totalSectionMatch = totalSection.replace(/[–—−]/g, '-').match(/-?\s*(\d+)/);
    const finalTotal = totalSectionMatch ? parseInt(totalSectionMatch[1], 10) : total;

    return { violationsCount: count, totalPointsDeducted: isNaN(finalTotal) ? 0 : finalTotal };
  }, [reportSections]);

  // Build deduction segments to visualize how the score dropped from 100
  const deductionSegments = useMemo(() => {
    const combined = `${String(reportSections['Rules Violated'] || '')}\n${String(
      reportSections['Analysis'] || ''
    )}`;

    const segments: { rule: string; points: number }[] = [];
    let currentRule: string | null = null;

    const lines = combined.split('\n');
    for (const raw of lines) {
      const line = raw.trim();
      if (!line) continue;
      if (line.includes('**Rule**:')) {
        currentRule = line
          .replace(/^-\s*/, '')
          .replace('**Rule**:', '')
          .replace(/"/g, '')
          .trim();
      } else if (line.includes('**Points Lost**:')) {
        const text = line.replace('**Points Lost**:', '').trim().replace(/[–—−]/g, '-');
        const m = text.match(/-?\s*(\d+)/);
        const points = m ? parseInt(m[1], 10) : 0;
        segments.push({ rule: currentRule || 'Rule', points });
        currentRule = null;
      }
    }

    // Sanitize and cap widths to 100%
    const total = segments.reduce((s, seg) => s + (isNaN(seg.points) ? 0 : seg.points), 0);
    const safeTotal = Math.max(0, Math.min(100, total));
    return segments.map(s => ({ ...s, points: isNaN(s.points) ? 0 : s.points })).slice(0, 12); // cap number for clarity
  }, [reportSections]);

  // Pre-render the Rules Violated section to prevent re-rendering issues
  const rulesViolatedContent = useMemo(() => {
    // Combine rules from both "Rules Violated" and "Analysis" sections
    let allRulesContent = '';
    
    // Get rules from Rules Violated section
    const rulesSection = reportSections['Rules Violated'];
    if (rulesSection) {
      allRulesContent += String(rulesSection) + '\n';
    }
    
    // Get rules from Analysis section (if any) - extract complete rule blocks
    const analysisSection = reportSections['Analysis'];
    if (analysisSection) {
      const analysisContent = String(analysisSection);
      const analysisLines = analysisContent.split('\n');
      
      // Extract complete rule blocks (rule + its associated details)
      for (let i = 0; i < analysisLines.length; i++) {
        const line = analysisLines[i];
        if (line.includes('**Rule**:') || (line.trim().startsWith('-') && line.includes('**Rule**'))) {
          // Found a rule, add it
          allRulesContent += line + '\n';
          
          // Look for the next few lines that belong to this rule
          for (let j = i + 1; j < analysisLines.length; j++) {
            const nextLine = analysisLines[j];
            const trimmedNext = nextLine.trim();
            
            // If we hit another rule, stop
            if (trimmedNext.includes('**Rule**:') || (trimmedNext.startsWith('-') && trimmedNext.includes('**Rule**'))) {
              break;
            }
            
            // If it's a detail line (Data Example, Points Lost, Impact) or empty line, include it
            if (trimmedNext.includes('**Data Example**:') || 
                trimmedNext.includes('**Points Lost**:') || 
                trimmedNext.includes('**Impact**:') ||
                (trimmedNext.startsWith('-') && (trimmedNext.includes('**Data Example**') || 
                                                trimmedNext.includes('**Points Lost**') || 
                                                trimmedNext.includes('**Impact**'))) ||
                !trimmedNext) {
              allRulesContent += nextLine + '\n';
            } else {
              // If it's some other content, stop (we've reached the end of this rule block)
              break;
            }
          }
        }
      }
    }
    
    if (!allRulesContent.trim()) {
      return null;
    }
    
    console.log('🔍 COMBINED RULES CONTENT:');
    console.log(allRulesContent);
    



    
    const lines = allRulesContent.split('\n');
    const ruleCards: React.JSX.Element[] = [];
    let currentCard: React.JSX.Element[] = [];
    let cardIndex = 0;
    
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      const trimmedLine = line.trim();
      

      
      // Skip empty lines or just bullet points
      if (!trimmedLine || trimmedLine === '-' || trimmedLine === '•') {
        continue;
      }
      
      // Check if this line starts a new rule (contains **Rule** or bullet point with Rule)
      const isRuleLine = trimmedLine.includes('**Rule**:') || (trimmedLine.startsWith('-') && trimmedLine.includes('**Rule**'));
      
      if (isRuleLine) {
        // Save previous card if it exists
        if (currentCard.length > 0) {

          ruleCards.push(
            <div 
              key={`card-${cardIndex}`} 
              className="rule-violation-card"
              style={{
                background: 'linear-gradient(135deg, rgba(96, 165, 250, 0.2), rgba(59, 130, 246, 0.1))',
                border: '2px solid rgba(96, 165, 250, 0.4)',
                borderLeft: '4px solid rgba(251, 191, 36, 1)',
                borderRadius: '12px',
                padding: '20px',
                marginBottom: '16px',
                boxShadow: '0 4px 12px rgba(96, 165, 250, 0.2)',
                backdropFilter: 'blur(10px)'
              }}
            >
              {currentCard}
            </div>
          );
          cardIndex++;
        }
        
        // Start new card with rule header
        let ruleText = trimmedLine.replace('**Rule**:', '').replace(/"/g, '').trim();
        // Handle bullet point format: "- **Rule**: "text""
        if (ruleText.startsWith('-')) {
          ruleText = ruleText.replace(/^-\s*\*\*Rule\*\*:\s*/, '').replace(/"/g, '').trim();
        }
        currentCard = [
          <div key={`header-${i}`} className="rule-header">
            <span className="rule-icon">⚡</span>
            <span className="rule-text">{ruleText}</span>
          </div>
        ];
      }
      // Check for data example
      else if (trimmedLine.includes('**Data Example**:') || (trimmedLine.startsWith('-') && trimmedLine.includes('**Data Example**'))) {
        let dataExample = trimmedLine.replace('**Data Example**:', '').trim();
        // Handle bullet point format: "- **Data Example**: text"
        if (dataExample.startsWith('-')) {
          dataExample = dataExample.replace(/^-\s*\*\*Data Example\*\*:\s*/, '').trim();
        }
        currentCard.push(
          <div 
            key={`data-${i}`} 
            className="rule-detail data-example"
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '12px',
              margin: '10px 0',
              padding: '12px 16px',
              borderRadius: '10px',
              background: 'linear-gradient(135deg, rgba(96, 165, 250, 0.15), rgba(59, 130, 246, 0.08))',
              borderLeft: '3px solid #60a5fa'
            }}
          >
            <span className="detail-label">Data Found:</span>
            <span className="detail-value">{dataExample}</span>
          </div>
        );
      }
      // Check for points lost
      else if (trimmedLine.includes('**Points Lost**:') || (trimmedLine.startsWith('-') && trimmedLine.includes('**Points Lost**'))) {
        let pointsLost = trimmedLine.replace('**Points Lost**:', '').trim();
        // Handle bullet point format: "- **Points Lost**: text"
        if (pointsLost.startsWith('-')) {
          pointsLost = pointsLost.replace(/^-\s*\*\*Points Lost\*\*:\s*/, '').trim();
        }
        currentCard.push(
          <div 
            key={`points-${i}`} 
            className="rule-detail points-lost"
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '12px',
              margin: '10px 0',
              padding: '12px 16px',
              borderRadius: '10px',
              background: 'linear-gradient(135deg, rgba(251, 113, 133, 0.15), rgba(239, 68, 68, 0.08))',
              borderLeft: '3px solid #fb7185'
            }}
          >
            <span className="detail-label">Points Lost:</span>
            <span className="detail-value points-value">{pointsLost}</span>
          </div>
        );
      }
      // Check for impact
      else if (trimmedLine.includes('**Impact**:') || (trimmedLine.startsWith('-') && trimmedLine.includes('**Impact**'))) {
        let impact = trimmedLine.replace('**Impact**:', '').trim();
        // Handle bullet point format: "- **Impact**: text"
        if (impact.startsWith('-')) {
          impact = impact.replace(/^-\s*\*\*Impact\*\*:\s*/, '').trim();
        }
        currentCard.push(
          <div 
            key={`impact-${i}`} 
            className="rule-detail impact"
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '12px',
              margin: '10px 0',
              padding: '12px 16px',
              borderRadius: '10px',
              background: 'linear-gradient(135deg, rgba(251, 191, 36, 0.15), rgba(245, 158, 11, 0.08))',
              borderLeft: '3px solid #fbbf24'
            }}
          >
            <span className="detail-label">Impact:</span>
            <span className="detail-value">{impact}</span>
          </div>
        );
      }
      // Any other content gets added to current card or as standalone
      else if (trimmedLine) {
        if (currentCard.length > 0) {
          currentCard.push(
            <p key={`content-${i}`} className="rule-other-content">
              {line}
            </p>
          );
        } else {
          // Standalone content (fallback)
          ruleCards.push(
            <p key={`standalone-${i}`} className="rule-other-content">
              {line}
            </p>
          );
        }
      }
    }
    
    // Don't forget the last card
    if (currentCard.length > 0) {
      ruleCards.push(
        <div 
          key={`card-${cardIndex}`} 
          className="rule-violation-card"
          style={{
            background: 'linear-gradient(135deg, rgba(96, 165, 250, 0.2), rgba(59, 130, 246, 0.1))',
            border: '2px solid rgba(96, 165, 250, 0.4)',
            borderLeft: '4px solid rgba(251, 191, 36, 1)',
            borderRadius: '12px',
            padding: '20px',
            marginBottom: '16px',
            boxShadow: '0 4px 12px rgba(96, 165, 250, 0.2)',
            backdropFilter: 'blur(10px)'
          }}
        >
          {currentCard}
        </div>
      );
    }
    

    return ruleCards;
  }, [reportSections]);

  // Early return after all hooks to comply with Rules of Hooks
  if (!isOpen) return null;

  return (
    <div className="quality-modal-overlay" onClick={onClose}>
      <div className="quality-modal-container" onClick={(e) => e.stopPropagation()}>
        <div className="quality-modal-header">
          <div className="quality-modal-title">
            <h2>Data Quality Analysis Results</h2>
            <span className="submission-id">Submission #{submissionId}</span>
          </div>
          <button className="quality-modal-close" onClick={onClose}>×</button>
        </div>

        <div className="quality-modal-content">
          {/* Quality Score Section with segmeter instead of radial gauge */}
          <div className="quality-score-section">
            <div className="score-container">
              <div className="segmeter">
                <div className="segmeter-track">
                  <div
                    className="segmeter-fill"
                    style={{ width: `${Math.max(0, Math.min(100, qualityScore))}%`, background: getScoreColor(qualityScore) }}
                  />
                  <div className="segmeter-grid" />
                </div>
                <div className="segmeter-meta">
                  <div className="segmeter-score">
                    <span className="segmeter-value">{qualityScore}</span>
                    <span className="segmeter-max">/100</span>
                  </div>
                  <div className="segmeter-label" style={{ color: getScoreColor(qualityScore) }}>
                    {getScoreLabel(qualityScore)}
                  </div>
                </div>
                {deductionSegments.length > 0 && (
                  <div className="deduction-timeline" aria-label="Deduction breakdown">
                    {deductionSegments.map((seg, idx) => (
                      <div
                        key={idx}
                        className="deduction-pill"
                        title={`${seg.rule} (−${seg.points})`}
                        style={{ flexBasis: `${Math.max(6, Math.min(30, seg.points))}%` }}
                      >
                        <span className="pill-points">-{seg.points}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="score-details">
                <h3 style={{ color: getScoreColor(qualityScore) }}>
                  {getScoreLabel(qualityScore)}
                </h3>
                <p className="score-description">Data Quality Score</p>
                <div className="stats-row">
                  <div className="stat-chip">
                    <span className="chip-label">Violations</span>
                    <span className="chip-value">{violationsCount}</span>
                  </div>
                  <div className="stat-chip warning">
                    <span className="chip-label">Points Deducted</span>
                    <span className="chip-value">-{totalPointsDeducted}</span>
                  </div>
                  <div className="stat-chip subtle">
                    <span className="chip-label">Submission</span>
                    <span className="chip-value">#{submissionId}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Quality Report Sections */}
          <div className="quality-report-section">
            <h3>Detailed Analysis</h3>
            <div className="report-sections">
              {Object.entries(reportSections).map(([sectionName, content]) => (
                <div key={sectionName} className="report-section">
                  <h4 className="section-title">{sectionName}</h4>
                  <div className="section-content">
                    {sectionName === 'Rules Violated' ? (
                      // Special formatting for Rules Violated section - PRE-RENDERED to prevent re-rendering issues
                      <div className="rules-violated-container">
                        {rulesViolatedContent}
                      </div>
                    ) : sectionName === 'Analysis' ? (
                      // Filter out rule-related content from Analysis section to avoid duplication
                      String(content).split('\n')
                        .filter(line => !line.includes('**Rule**:') && 
                                       !line.includes('**Data Example**:') && 
                                       !line.includes('**Points Lost**:') && 
                                       !line.includes('**Impact**:') &&
                                       !(line.trim().startsWith('-') && (line.includes('**Rule**') || line.includes('**Data Example**') || line.includes('**Points Lost**') || line.includes('**Impact**'))))
                        .map((line, index) => (
                          <p key={index} className={line.trim().startsWith('•') || line.trim().startsWith('-') ? 'bullet-point' : ''}>
                            {line}
                          </p>
                        ))
                    ) : (
                      // Regular formatting for other sections
                      String(content).split('\n').map((line, index) => (
                      <p key={index} className={line.trim().startsWith('•') || line.trim().startsWith('-') ? 'bullet-point' : ''}>
                        {line}
                      </p>
                      ))
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="quality-modal-footer">
          <button className="quality-modal-close-btn" onClick={onClose}>
            Close Analysis
          </button>
        </div>
      </div>
    </div>
  );
}, (prevProps, nextProps) => {
  // Custom comparison function for React.memo
  // Only re-render if the actual content props change, ignore function props
  const isEqual = (
    prevProps.isOpen === nextProps.isOpen &&
    prevProps.qualityScore === nextProps.qualityScore &&
    prevProps.qualityReport === nextProps.qualityReport &&
    prevProps.submissionId === nextProps.submissionId
  );
  
  console.log('🔍 DEBUG: React.memo comparison - should skip render:', isEqual);
  return isEqual;
});

// Add display name for debugging
QualityCheckResultModal.displayName = 'QualityCheckResultModal';

export default QualityCheckResultModal;
